// «Inteligencia de medios» del Tablero del Presidente (Orion for Government).
// Síntesis del día y tendencias del mes de lo que publican los medios locales,
// nacionales e internacionales. Todo lo calcula el servidor (apps/gov_executive/medios.py):
// temas y tono son estimados por reglas y cada nota enlaza a su fuente original.
// Herramienta de gobierno: mide la conversación pública sobre la gestión, no es electoral.

import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownRight, ArrowRight, ArrowUpRight, ExternalLink, Globe2, Landmark, MessageCircle, Newspaper, Radar,
  RefreshCw, Sparkles, X,
} from 'lucide-react';

import { ChatPane } from '@/components/assistant/ChatPane';
import { OsApp, OsTabs, osActionBtn } from '@/modules/orion-shell/OsApp';
import { Card, EmptyState, ErrorNote, Loading } from '@/modules/orion-shell/ui';
import { fmt, govFetch } from '@/modules/gov-health';

type Alcance = 'local' | 'nacional' | 'internacional';
type Tono = 'negativo' | 'neutral' | 'positivo';
interface Nota { titulo: string; url: string; fuente: string; alcance: Alcance; publicado_at: string; temas: string[]; tono: Tono; municipal: boolean; resumen: string }
interface Tema {
  tema: string; etiqueta: string; dependencia: string; notas: number; ultimos_7: number; cambio_pct: number | null;
  tendencia: 'sube' | 'baja' | 'estable'; tono: Record<Tono, number>; negativo_pct: number; medios: number;
  participacion_7d_pct: number; participacion_previa_pct: number; serie_semanal: number[]; serie_diaria: number[];
}
interface Historia {
  titulo: string; notas: number; medios: string[]; alcances: Alcance[]; tema: string; tema_etiqueta: string; dependencia: string;
  tono: Tono; municipal: boolean; actores: string[]; ultima: string; puntaje: number; accion_sugerida: string; fuentes: Nota[];
}
interface Actor { clave: string; nombre: string; cargo: string; municipal: boolean; menciones: number; ultimos_7: number; tono: Record<Tono, number>; serie_diaria: number[]; medios: number; recientes: Nota[] }
interface Intel {
  estado: 'ok' | 'no_configurado'; periodo: { desde: string; hasta: string; dias: number }; dias: string[];
  total: number; medios: number; por_alcance: Partial<Record<Alcance, number>>; serie_total: number[]; serie_negativa: number[];
  temas: Tema[]; tema_en_ascenso: { tema: string; etiqueta: string; cambio_pct: number } | null;
  historias: Historia[]; prioridades: Historia[]; actores: Actor[]; recientes: Record<Alcance, Nota[]>;
  fuentes: { nombre: string; alcance: Alcance; notas: number }[];
  sync: { last_sync_at: string | null; en_curso?: boolean; errores?: string[] }; sugerencias: string[]; metodo: string; aviso: string;
}

const TONO: Record<Tono, { label: string; cls: string; bar: string }> = {
  negativo: { label: 'Negativo', cls: 'bg-red-500/15 text-red-700 dark:text-red-300', bar: 'bg-red-500' },
  neutral: { label: 'Neutral', cls: 'bg-muted text-muted-foreground', bar: 'bg-slate-400 dark:bg-slate-500' },
  positivo: { label: 'Positivo', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300', bar: 'bg-emerald-500' },
};
const ALCANCE: Record<Alcance, string> = { local: 'Local', nacional: 'Nacional', internacional: 'Internacional' };

const seguro = (u: string) => (/^https?:\/\//i.test(u) ? u : undefined);   // nunca javascript: ni data:
const hace = (iso: string) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60);
  return h < 36 ? `hace ${h} h` : new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
};

export function InteligenciaPanel() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [dias, setDias] = useState<'7' | '30'>('30');
  const [chat, setChat] = useState(false);
  const [tema, setTema] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['gov-executive', 'inteligencia', dias],
    queryFn: () => govFetch<Intel>(`/api/v1/admin/gov-executive/inteligencia?dias=${dias}`),
    retry: false, staleTime: 5 * 60_000,
  });
  const actualizar = useMutation({
    mutationFn: () => govFetch(`/api/v1/admin/gov-executive/inteligencia/actualizar`, { method: 'POST' }),
    // La lectura corre en el servidor en segundo plano: volver a pedir el panel en unos segundos.
    onSuccess: () => { [8000, 25000].forEach((ms) => setTimeout(() => qc.invalidateQueries({ queryKey: ['gov-executive', 'inteligencia'] }), ms)); },
  });
  useEffect(() => {
    if (!chat) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setChat(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chat]);
  const d = q.data;
  return (
    <OsApp
      icon={Radar}
      title="Inteligencia de medios"
      subtitle={d?.estado === 'ok' ? `${fmt(d.total)} notas de ${d.medios} medios · ${dias === '30' ? 'últimos 30 días' : 'últimos 7 días'}` : 'Síntesis y tendencias de los medios'}
      tabs={<OsTabs label="Vista" value="inteligencia" onChange={(v) => v === 'tablero' && navigate('/m/gov-executive/tablero')}
        items={[{ key: 'tablero', label: 'Tablero', icon: Landmark }, { key: 'inteligencia', label: 'Inteligencia', icon: Radar }]} />}
      actions={
        <span className="flex items-center gap-2">
          <span className="hidden rounded-xl border border-border p-0.5 sm:inline-flex" role="group" aria-label="Periodo">
            {(['7', '30'] as const).map((k) => (
              <button key={k} type="button" aria-pressed={dias === k} onClick={() => setDias(k)}
                className={`h-8 rounded-lg px-2.5 text-xs ${dias === k ? 'bg-primary/10 font-semibold text-primary' : 'text-muted-foreground hover:bg-muted'}`}>{k} días</button>
            ))}
          </span>
          <button type="button" className={osActionBtn} disabled={actualizar.isPending} onClick={() => actualizar.mutate()} title="Leer ahora las fuentes">
            <RefreshCw size={14} className={actualizar.isPending || d?.sync?.en_curso ? 'animate-spin' : ''} /> <span className="hidden sm:inline">Actualizar</span>
          </button>
          <button type="button" onClick={() => setChat(true)} className="os-btn-brand inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-[13px] font-semibold">
            <MessageCircle size={15} /> <span className="hidden sm:inline">Pregúntale a Orión</span><span className="sm:hidden">Orión</span>
          </button>
        </span>
      }
    >
      {q.isLoading ? <Loading label="Leyendo los medios…" /> : q.isError ? (
        (q.error as any)?.status === 403
          ? <EmptyState icon={<Radar size={18} />} title="Esta vista es para la Presidencia">Pide el rol «presidente» a la administración.</EmptyState>
          : (q.error as any)?.status === 404
            ? <EmptyState icon={<Radar size={18} />} title="El Tablero del Presidente no está activo para esta organización" />
            : <ErrorNote>{(q.error as Error).message}</ErrorNote>
      ) : d!.estado !== 'ok' ? (
        <EmptyState icon={<Newspaper size={18} />} title="Monitor de medios no configurado">La plataforma configura las fuentes de la organización (gov_medios_org).</EmptyState>
      ) : (
        <div className="flex flex-col gap-4">
          <Briefing d={d!} />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label={`Notas · ${dias} días`} value={fmt(d!.total)} sub={`${fmt(d!.por_alcance.local ?? 0)} locales · ${fmt(d!.por_alcance.nacional ?? 0)} nacionales · ${fmt(d!.por_alcance.internacional ?? 0)} internacionales`} />
            <Kpi label="Medios monitoreados" value={fmt(d!.medios)} sub={d!.sync.last_sync_at ? `actualizado ${hace(d!.sync.last_sync_at)}` : undefined} />
            {d!.actores[0] && <Kpi label={`Menciones · ${d!.actores[0].nombre}`} value={fmt(d!.actores[0].menciones)}
              sub={`${fmt(d!.actores[0].ultimos_7)} esta semana · ${pct(d!.actores[0].tono.negativo, d!.actores[0].menciones)}% negativas`} />}
            <Kpi label="Tema en ascenso" value={d!.tema_en_ascenso?.etiqueta ?? '—'}
              sub={d!.tema_en_ascenso ? `+${d!.tema_en_ascenso.cambio_pct}% de la conversación vs. semanas previas` : 'ningún tema crece de forma marcada'} small />
          </div>
          <div className="grid gap-4 lg:grid-cols-5">
            <Card title="Cobertura diaria" className="lg:col-span-3">
              <Cobertura d={d!} />
            </Card>
            <div className="flex flex-col gap-4 lg:col-span-2">
              {d!.actores.filter((a) => a.municipal).slice(0, 2).map((a) => <ActorCard key={a.clave} a={a} />)}
            </div>
          </div>
          <Card title="Tendencias del mes por tema" action={<span className="text-[11px] text-muted-foreground">clic en un tema para ver sus notas</span>}>
            <TemasTabla temas={d!.temas} sel={tema} onSel={setTema} />
            {tema && <NotasTema tema={tema} dias={dias} etiqueta={d!.temas.find((t) => t.tema === tema)?.etiqueta ?? tema} onClose={() => setTema(null)} />}
          </Card>
          <div className="grid gap-4 lg:grid-cols-5">
            <Card title="Historias de la semana" className="lg:col-span-3">
              <ol className="flex flex-col divide-y divide-border">
                {d!.historias.slice(0, 8).map((h) => <HistoriaRow key={h.titulo + h.ultima} h={h} />)}
              </ol>
            </Card>
            <Card title={<span className="inline-flex items-center gap-1.5"><Globe2 size={15} className="text-primary" /> Qué dicen los medios</span>} className="lg:col-span-2">
              <Recientes d={d!} />
            </Card>
          </div>
          <Card title="Cómo se calcula">
            <p className="text-sm text-foreground">{d!.metodo}</p>
            <p className="mt-2 text-xs text-muted-foreground">{d!.aviso}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {d!.fuentes.map((f, i) => (
                <span key={f.nombre + i} className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground">
                  {f.nombre} · {ALCANCE[f.alcance]}{f.notas ? ` · ${fmt(f.notas)}` : ''}
                </span>
              ))}
            </div>
          </Card>
        </div>
      )}
      {chat && createPortal(
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 sm:items-center" role="dialog" aria-modal="true" aria-label="Pregúntale a Orión">
          <div className="flex h-[85dvh] w-full flex-col overflow-hidden rounded-t-3xl border border-border bg-background shadow-2xl sm:h-[78vh] sm:max-w-xl sm:rounded-3xl">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
              <MessageCircle size={16} className="text-primary" />
              <p className="flex-1 text-sm font-semibold">Pregúntale a Orión · medios</p>
              <button type="button" onClick={() => setChat(false)} aria-label="Cerrar" className="grid h-9 w-9 place-items-center rounded-xl hover:bg-muted"><X size={16} /></button>
            </div>
            <div className="min-h-0 flex-1">
              <ChatPane
                startFresh hideThinking focusTools="gov-executive"
                messagePrefix="[Inteligencia de medios del Presidente] Usa la inteligencia de medios del servidor; cita el medio de cada dato, di que tono y temas son estimados por reglas y que las acciones son propuestas. Es una herramienta de gobierno: no hagas análisis electoral."
                emptyTitle="¿Qué quieres saber de los medios?"
                emptySubtitle="Orión responde con la síntesis y las notas del servidor, citando la fuente."
                suggestions={d?.sugerencias?.length ? d.sugerencias : ['Dame la síntesis de hoy en 5 puntos', '¿Qué tema está creciendo y quién debe responder?', '¿Qué dicen los medios nacionales del municipio?']}
                webChatHeaders={(() => { const o = localStorage.getItem('org_id'); return o ? { 'x-org-id': o } : {}; })()}
              />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </OsApp>
  );
}

const pct = (a: number, b: number) => (b ? Math.round((100 * a) / b) : 0);

function Kpi({ label, value, sub, small }: { label: string; value: ReactNode; sub?: string; small?: boolean }) {
  return (
    <div className="orion-card min-w-0 rounded-[18px] border border-border bg-card p-4">
      <p className="truncate text-xs font-medium text-muted-foreground">{label}</p>
      <p className={`mt-1 truncate font-semibold leading-tight tracking-tight text-foreground ${small ? 'text-lg' : 'text-[26px] tabular-nums'}`}>{value}</p>
      {sub && <p className="mt-1.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function TonoChip({ t }: { t: Tono }) {
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONO[t].cls}`}>{TONO[t].label}</span>;
}

function TonoBar({ tono, total }: { tono: Record<Tono, number>; total: number }) {
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted" role="img"
      aria-label={`Negativo ${pct(tono.negativo, total)}%, neutral ${pct(tono.neutral, total)}%, positivo ${pct(tono.positivo, total)}%`}>
      {(['negativo', 'neutral', 'positivo'] as Tono[]).map((k) => <span key={k} className={TONO[k].bar} style={{ width: `${pct(tono[k], total)}%` }} />)}
    </div>
  );
}

function Briefing({ d }: { d: Intel }) {
  const hoy = new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
  return (
    <section aria-label="Briefing del día" className="rounded-[22px] border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles size={16} className="text-primary" />
        <h2 className="text-xs font-semibold uppercase tracking-wide text-primary">Briefing del día · {hoy}</h2>
        <span className="text-[11px] text-muted-foreground">Las 3 historias que más pesan para la gestión municipal</span>
      </div>
      <ol className="mt-3 grid gap-3 lg:grid-cols-3">
        {d.prioridades.map((h, i) => (
          <li key={h.titulo + h.ultima} className="flex min-w-0 flex-col gap-2 rounded-2xl border border-border bg-card p-3.5">
            <div className="flex items-start gap-2.5">
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{i + 1}</span>
              <p className="min-w-0 text-[15px] font-semibold leading-snug">{h.titulo}</p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
              <TonoChip t={h.tono} />
              <span className="rounded-full bg-muted px-2 py-0.5 font-medium">{h.tema_etiqueta}</span>
              <span className="text-muted-foreground">{h.medios.length} medio(s) · {h.notas} nota(s) · {h.alcances.map((a) => ALCANCE[a]).join(', ')}</span>
            </div>
            <p className="rounded-xl bg-muted/60 px-3 py-2 text-xs leading-relaxed"><span className="font-semibold">{h.dependencia}:</span> {h.accion_sugerida}</p>
            <ul className="mt-auto flex flex-col gap-1">
              {h.fuentes.slice(0, 2).map((f) => (
                <li key={f.url}><a href={seguro(f.url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline">
                  <ExternalLink size={11} />{f.fuente} · {hace(f.publicado_at)}</a></li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
      <p className="mt-2 text-[11px] text-muted-foreground">Propuestas para el equipo del Presidente. Abre la nota original antes de decidir.</p>
    </section>
  );
}

function Cobertura({ d }: { d: Intel }) {
  const max = Math.max(1, ...d.serie_total);
  const n = d.serie_total.length;
  return (
    <div>
      <div className="flex h-40 items-end gap-[3px]" role="img" aria-label={`Notas por día; máximo ${max}`}>
        {d.serie_total.map((v, i) => (
          <div key={d.dias[i]} className="group relative flex h-full min-w-0 flex-1 flex-col justify-end" title={`${d.dias[i]}: ${v} notas, ${d.serie_negativa[i]} negativas`}>
            <div className="w-full rounded-t-[3px] bg-primary/25" style={{ height: `${(100 * (v - d.serie_negativa[i])) / max}%` }} />
            <div className="w-full bg-red-500/80" style={{ height: `${(100 * d.serie_negativa[i]) / max}%` }} />
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-muted-foreground">
        <span>{new Date(d.dias[0] + 'T12:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}</span>
        <span>{new Date(d.dias[Math.floor(n / 2)] + 'T12:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}</span>
        <span>hoy</span>
      </div>
      <div className="mt-2 flex gap-4 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-primary/25" />Notas</span>
        <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-red-500/80" />Tono negativo</span>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">Cada medio publica en su feed una profundidad distinta de días; por eso las tendencias se miden en participación del tema, no en número de notas.</p>
    </div>
  );
}

function Spark({ data, className = '' }: { data: number[]; className?: string }) {
  const max = Math.max(1, ...data);
  const w = 120, h = 28;
  const pts = data.map((v, i) => `${(i / Math.max(1, data.length - 1)) * w},${h - (v / max) * (h - 2) - 1}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`h-7 w-full ${className}`} preserveAspectRatio="none" aria-hidden>
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function ActorCard({ a }: { a: Actor }) {
  return (
    <Card title={<span className="truncate">{a.nombre}</span>} action={<span className="text-[11px] text-muted-foreground">{a.cargo}</span>}>
      <div className="flex items-end gap-4">
        <div><p className="text-2xl font-semibold tabular-nums">{fmt(a.menciones)}</p><p className="text-[11px] text-muted-foreground">menciones · {a.medios} medios</p></div>
        <div className="min-w-0 flex-1 text-primary"><Spark data={a.serie_diaria} /></div>
      </div>
      <div className="mt-2"><TonoBar tono={a.tono} total={a.menciones} /></div>
      <p className="mt-1 text-[11px] text-muted-foreground">{pct(a.tono.negativo, a.menciones)}% negativo · {pct(a.tono.positivo, a.menciones)}% positivo · resto neutral</p>
      <ul className="mt-2 flex flex-col gap-1.5">
        {a.recientes.slice(0, 3).map((n) => <NotaLink key={n.url} n={n} />)}
      </ul>
    </Card>
  );
}

function NotaLink({ n }: { n: Nota }) {
  return (
    <li className="min-w-0">
      <a href={seguro(n.url)} target="_blank" rel="noopener noreferrer" className="group block rounded-lg px-1 py-0.5 hover:bg-muted">
        <p className="line-clamp-2 text-[13px] leading-snug text-foreground group-hover:underline">{n.titulo}</p>
        <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${TONO[n.tono].bar}`} aria-label={TONO[n.tono].label} />
          {n.fuente} · {hace(n.publicado_at)}
        </p>
      </a>
    </li>
  );
}

function Tendencia({ t }: { t: Tema }) {
  if (t.cambio_pct == null) return <span className="text-xs text-muted-foreground">—</span>;
  const Icon = t.tendencia === 'sube' ? ArrowUpRight : t.tendencia === 'baja' ? ArrowDownRight : ArrowRight;
  // Subir/bajar es volumen de conversación, no algo bueno o malo: el tono va en su propia columna.
  const cls = t.tendencia === 'estable' ? 'text-muted-foreground' : 'text-primary';
  return <span className={`inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums ${cls}`}><Icon size={14} />{t.cambio_pct > 0 ? '+' : ''}{t.cambio_pct}%</span>;
}

function TemasTabla({ temas, sel, onSel }: { temas: Tema[]; sel: string | null; onSel: (t: string | null) => void }) {
  const max = Math.max(1, ...temas.map((t) => t.notas));
  return (
    <div className="-mx-1 overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm">
        <thead><tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
          <th className="px-1 py-1.5 font-semibold">Tema</th><th className="px-1 py-1.5 font-semibold">Notas</th>
          <th className="px-1 py-1.5 font-semibold">Últimos 7 días</th><th className="px-1 py-1.5 font-semibold" title="Cambio en la parte de la conversación que ocupa el tema">Tendencia</th>
          <th className="w-40 px-1 py-1.5 font-semibold">Tono</th><th className="w-28 px-1 py-1.5 font-semibold">Diario</th>
        </tr></thead>
        <tbody>
          {temas.map((t) => (
            <tr key={t.tema} onClick={() => onSel(sel === t.tema ? null : t.tema)} aria-selected={sel === t.tema}
              className={`cursor-pointer border-t border-border hover:bg-muted/50 ${sel === t.tema ? 'bg-primary/5' : ''}`}>
              <td className="px-1 py-2"><p className="font-medium">{t.etiqueta}</p><p className="text-[11px] text-muted-foreground">{t.dependencia}</p></td>
              <td className="px-1 py-2">
                <div className="flex items-center gap-2"><span className="w-10 tabular-nums">{fmt(t.notas)}</span>
                  <span className="h-1.5 w-20 rounded-full bg-muted"><span className="block h-1.5 rounded-full bg-primary/60" style={{ width: `${(100 * t.notas) / max}%` }} /></span></div>
              </td>
              <td className="px-1 py-2 tabular-nums">{fmt(t.ultimos_7)} <span className="text-[11px] text-muted-foreground">· {t.participacion_7d_pct}%</span></td>
              <td className="px-1 py-2"><Tendencia t={t} /></td>
              <td className="px-1 py-2"><TonoBar tono={t.tono} total={t.notas} /><p className="mt-0.5 text-[11px] text-muted-foreground">{t.negativo_pct}% negativo</p></td>
              <td className="px-1 py-2 text-primary"><Spark data={t.serie_diaria} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NotasTema({ tema, dias, etiqueta, onClose }: { tema: string; dias: string; etiqueta: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['gov-executive', 'notas', tema, dias],
    queryFn: () => govFetch<{ total: number; notas: Nota[] }>(`/api/v1/admin/gov-executive/inteligencia/notas?tema=${tema}&dias=${dias}&limite=12`),
  });
  return (
    <div className="mt-3 rounded-2xl border border-primary/25 bg-primary/5 p-3">
      <div className="mb-2 flex items-center gap-2">
        <p className="flex-1 text-sm font-semibold">{etiqueta}{q.data ? ` · ${fmt(q.data.total)} notas` : ''}</p>
        <button type="button" onClick={onClose} aria-label="Cerrar notas del tema" className="grid h-7 w-7 place-items-center rounded-lg hover:bg-muted"><X size={14} /></button>
      </div>
      {q.isLoading ? <Loading /> : <ul className="grid gap-1.5 sm:grid-cols-2">{q.data?.notas.map((n) => <NotaLink key={n.url} n={n} />)}</ul>}
    </div>
  );
}

function HistoriaRow({ h }: { h: Historia }) {
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-start gap-2">
        <p className="min-w-0 flex-1 text-sm font-semibold leading-snug">{h.titulo}</p>
        <TonoChip t={h.tono} />
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">
        {h.tema_etiqueta} · {h.medios.join(', ')} · {h.notas} nota(s) · {hace(h.ultima)}{h.municipal ? ' · gestión municipal' : ''}
      </p>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
        {h.fuentes.slice(0, 3).map((f) => (
          <a key={f.url} href={seguro(f.url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"><ExternalLink size={10} />{f.fuente}</a>
        ))}
      </div>
    </li>
  );
}

function Recientes({ d }: { d: Intel }) {
  const [al, setAl] = useState<Alcance>('local');
  const items = d.recientes[al] ?? [];
  return (
    <div>
      <div className="mb-2 flex gap-1" role="tablist" aria-label="Alcance">
        {(['local', 'nacional', 'internacional'] as Alcance[]).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={al === k} onClick={() => setAl(k)}
            className={`h-8 rounded-full px-3 text-xs ${al === k ? 'bg-primary text-primary-foreground font-semibold' : 'border border-border hover:bg-muted'}`}>
            {ALCANCE[k]} <span className="opacity-70">{fmt(d.por_alcance[k] ?? 0)}</span>
          </button>
        ))}
      </div>
      {items.length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">Sin notas de este alcance en el periodo.</p> : (
        <ul className="flex max-h-[420px] flex-col gap-1.5 overflow-y-auto pr-1">{items.slice(0, 14).map((n) => <NotaLink key={n.url} n={n} />)}</ul>
      )}
      {al !== 'local' && <p className="mt-2 text-[11px] text-muted-foreground">{al === 'nacional' ? 'Lo que toca al municipio y la agenda nacional de seguridad, agua, economía y finanzas.' : 'Lo que medios internacionales publican sobre México y el municipio.'}</p>}
    </div>
  );
}
